/**
 * Impact hub — scroll track + sticky viewport (no GSAP).
 *
 * @package HelloElementorChild
 */

import {
  animateCounter,
  prefersReducedMotion,
  setCounterFinal,
  killCounterFrames,
} from './camst-counter.js';

var INTRO_IMAGE_INTERVAL_MS = 1500;
var INTRO_THRESHOLD_DESKTOP = 0.2;
var INTRO_THRESHOLD_MOBILE = 0.1;
var IMPACT_SCROLL_DESKTOP_MIN = 200;
var IMPACT_SCROLL_MOBILE_MIN = 150;

function isImpactDesktop() {
  return window.matchMedia('(min-width: 1024px)').matches;
}

function getImpactScrollLength(root, isMobile) {
  var attr = isMobile
    ? root.getAttribute('data-scroll-mobile')
    : root.getAttribute('data-scroll-desktop');
  var min = isMobile ? IMPACT_SCROLL_MOBILE_MIN : IMPACT_SCROLL_DESKTOP_MIN;
  var parsed = parseInt(attr || String(min), 10);

  return Number.isNaN(parsed) ? min : Math.max(min, parsed);
}

function getIntroThreshold(isMobile) {
  return isMobile ? INTRO_THRESHOLD_MOBILE : INTRO_THRESHOLD_DESKTOP;
}

function getTrackMetrics(track) {
  var rect = track.getBoundingClientRect();
  var scrollTop = window.scrollY || window.pageYOffset;
  var trackTop = scrollTop + rect.top;
  var trackHeight = track.offsetHeight;
  var viewportHeight = window.innerHeight;
  var scrollRange = Math.max(1, trackHeight - viewportHeight);

  return {
    trackTop: trackTop,
    scrollRange: scrollRange,
  };
}

function getScrollProgress(root) {
  var track = root.querySelector('[data-camst-impact-track]');

  if (!track) {
    return 0;
  }

  var metrics = getTrackMetrics(track);
  var scrollY = window.scrollY || window.pageYOffset;
  var raw = (scrollY - metrics.trackTop) / metrics.scrollRange;

  return Math.max(0, Math.min(1, raw));
}

function getImpactIndexFromProgress(progress, count, introThreshold) {
  if (count <= 1) {
    return 0;
  }

  if (progress < introThreshold) {
    return 0;
  }

  var scaled = (progress - introThreshold) / (1 - introThreshold);
  var segment = 1 / count;
  var index = Math.floor((scaled + segment * 0.2) / segment);

  return Math.max(0, Math.min(count - 1, index));
}

function getNavScrollProgress(index, count, isMobile) {
  var introOffset = getIntroThreshold(isMobile);
  var scaleFactor = 1 - introOffset;

  if (index < 0) {
    return 0;
  }

  return introOffset + (scaleFactor / count) * (index + 0.1);
}

function scrollToImpactProgress(root, progress, reducedMotion) {
  var track = root.querySelector('[data-camst-impact-track]');

  if (!track) {
    return;
  }

  var metrics = getTrackMetrics(track);
  var target = metrics.trackTop + progress * metrics.scrollRange;
  var useInstant = reducedMotion || prefersReducedMotion();

  window.scrollTo({
    top: target,
    behavior: useInstant ? 'auto' : 'smooth',
  });
}

function setImpactCounterFinal(counterEl, value) {
  setCounterFinal(counterEl, value);
}

function setImpactPhase(root, isIntro, isSlider) {
  root.classList.toggle('is-intro', isIntro);
  root.classList.toggle('is-slider', isSlider);
}

function setImpactActiveIndex(root, index, reducedMotion, options) {
  var opts = options || {};
  var slides = root.querySelectorAll('[data-camst-impact-slide]');
  var kpis = root.querySelectorAll('[data-camst-impact-kpi]');
  var navItems = root.querySelectorAll('[data-camst-impact-nav-item]');
  var progress = root.querySelector('[data-camst-impact-nav-progress]');
  var count = slides.length;
  var isIntro = root.classList.contains('is-intro');

  if (!count) {
    return;
  }

  var safeIndex = Math.max(0, Math.min(index, count - 1));

  slides.forEach(function (slide, slideIndex) {
    var visible = isIntro
      ? slideIndex === root._camstIntroImageIndex
      : slideIndex === safeIndex;

    slide.classList.toggle('is-visible', visible);
  });

  kpis.forEach(function (kpi, kpiIndex) {
    var isActive = kpiIndex === safeIndex;
    kpi.classList.toggle('is-active', isActive);

    if (isActive && !isIntro) {
      var counterEl = kpi.querySelector('[data-camst-impact-counter]');

      if (opts.instantCounter) {
        setImpactCounterFinal(counterEl, kpi.getAttribute('data-kpi-value'));
      } else if (opts.animateCounter !== false) {
        animateCounter(counterEl, kpi.getAttribute('data-kpi-value'), reducedMotion);
      }
    }
  });

  navItems.forEach(function (item, itemIndex) {
    var isActive = itemIndex === safeIndex;
    item.classList.toggle('is-active', isActive);
    item.setAttribute('aria-current', isActive ? 'true' : 'false');
  });

  if (progress && count > 0 && !isIntro) {
    progress.style.height = 100 / count + '%';
    progress.style.transform = 'translateY(' + safeIndex * 100 + '%)';
  }

  root.dataset.activeIndex = String(safeIndex);
  root._camstActiveIndex = safeIndex;
}

function positionImpactMarkers(root) {
  var track = root.querySelector('[data-camst-impact-track]');
  var markersWrap = root.querySelector('.camst-impact__markers');

  if (!track || !markersWrap) {
    return;
  }

  var isMobile = !isImpactDesktop();
  var count = parseInt(root.getAttribute('data-count') || '0', 10);
  var scrollLength = getImpactScrollLength(root, isMobile);
  var trackHeight = track.offsetHeight;
  var viewportHeight = window.innerHeight;
  var scrollRange = Math.max(1, trackHeight - viewportHeight);

  markersWrap.style.height = trackHeight + 'px';
  markersWrap.style.width = '1px';
  markersWrap.style.position = 'absolute';
  markersWrap.style.top = '0';
  markersWrap.style.left = '0';
  markersWrap.style.pointerEvents = 'none';
  markersWrap.style.visibility = 'hidden';

  root.querySelectorAll('[data-camst-impact-marker]').forEach(function (marker) {
    var progress;

    if (marker.hasAttribute('data-progress')) {
      progress = 0;
    } else {
      var index = parseInt(marker.getAttribute('data-index') || '0', 10);
      progress = getNavScrollProgress(index, count, isMobile);
    }

    var topPx = progress * scrollRange;
    marker.style.top = topPx + 'px';
    marker.style.left = '0';
  });
}

function clearImpactNavLock(root) {
  if (typeof root._camstNavLockIndex === 'number') {
    delete root._camstNavLockIndex;
  }
}

function isImpactNavLockSettled(root, progress) {
  if (typeof root._camstNavLockIndex !== 'number') {
    return true;
  }

  var count = parseInt(root.getAttribute('data-count') || '0', 10);
  var isMobile = !isImpactDesktop();
  var targetProgress = getNavScrollProgress(root._camstNavLockIndex, count, isMobile);

  return Math.abs(progress - targetProgress) < 0.025;
}

function updateImpactFromScroll(root, reducedMotion) {
  var count = parseInt(root.getAttribute('data-count') || '0', 10);
  var isMobile = !isImpactDesktop();
  var introThreshold = getIntroThreshold(isMobile);
  var progress = getScrollProgress(root);

  if (typeof root._camstNavLockIndex === 'number') {
    if (isImpactNavLockSettled(root, progress)) {
      clearImpactNavLock(root);
    } else {
      return;
    }
  }

  var isIntro = progress < introThreshold;
  var isSlider = !isIntro;
  var wasIntro = root._camstWasIntro === true;

  root._camstWasIntro = isIntro;

  if (isIntro) {
    startIntroImageLoop(root, reducedMotion);
  } else {
    stopIntroImageLoop(root);
  }

  setImpactPhase(root, isIntro, isSlider);

  if (isIntro) {
    setImpactActiveIndex(root, root._camstActiveIndex || 0, reducedMotion, { animateCounter: false });
    return;
  }

  var nextIndex = getImpactIndexFromProgress(progress, count, introThreshold);

  if (wasIntro) {
    setImpactActiveIndex(root, nextIndex, reducedMotion);
    return;
  }

  if (nextIndex !== root._camstActiveIndex) {
    setImpactActiveIndex(root, nextIndex, reducedMotion);
  }
}

function bindImpactScroll(root, reducedMotion) {
  var ticking = false;

  function onScrollOrResize() {
    if (ticking) {
      return;
    }

    ticking = true;

    requestAnimationFrame(function () {
      ticking = false;
      positionImpactMarkers(root);
      updateImpactFromScroll(root, reducedMotion);
    });
  }

  root._camstImpactOnScroll = onScrollOrResize;
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize, { passive: true });
  onScrollOrResize();
}

function unbindImpactScroll(root) {
  if (root._camstImpactOnScroll) {
    window.removeEventListener('scroll', root._camstImpactOnScroll);
    window.removeEventListener('resize', root._camstImpactOnScroll);
    root._camstImpactOnScroll = null;
  }
}

function startIntroImageLoop(root, reducedMotion) {
  if (root._camstIntroTimer || reducedMotion) {
    return;
  }

  var count = parseInt(root.getAttribute('data-count') || '0', 10);

  if (count < 2) {
    return;
  }

  root._camstIntroTimer = window.setInterval(function () {
    if (!root.classList.contains('is-intro')) {
      return;
    }

    root._camstIntroImageIndex = (root._camstIntroImageIndex + 1) % count;
    setImpactActiveIndex(root, root._camstActiveIndex, reducedMotion, { animateCounter: false });
  }, INTRO_IMAGE_INTERVAL_MS);
}

function stopIntroImageLoop(root) {
  if (root._camstIntroTimer) {
    window.clearInterval(root._camstIntroTimer);
    root._camstIntroTimer = null;
  }
}

export function teardownCamstImpact(scope) {
  var roots = [];

  if (!scope) {
    return;
  }

  if (scope.matches && scope.matches('[data-camst-impact]')) {
    roots = [scope];
  } else {
    roots = Array.prototype.slice.call(scope.querySelectorAll('[data-camst-impact]'));
  }

  roots.forEach(function (root) {
    unbindImpactScroll(root);
    stopIntroImageLoop(root);
    killCounterFrames(root, '[data-camst-impact-counter]');

    delete root.dataset.camstImpactInit;
    delete root.dataset.activeIndex;
    delete root._camstActiveIndex;
    delete root._camstIntroImageIndex;
    delete root._camstNavigating;
    delete root._camstWasIntro;
    clearImpactNavLock(root);

    root.classList.remove('is-intro', 'is-slider');
  });
}

export function initCamstImpact(scope) {
  var roots = [];

  if (!scope) {
    roots = Array.prototype.slice.call(document.querySelectorAll('[data-camst-impact]'));
  } else if (scope.matches && scope.matches('[data-camst-impact]')) {
    roots = [scope];
  } else {
    roots = Array.prototype.slice.call(scope.querySelectorAll('[data-camst-impact]'));
  }

  roots.forEach(function (root) {
    if (root.dataset.camstImpactInit === '1') {
      return;
    }

    var track = root.querySelector('[data-camst-impact-track]');
    var slides = root.querySelectorAll('[data-camst-impact-slide]');
    var count = slides.length;

    if (!track || !count) {
      return;
    }

    var reducedMotion = prefersReducedMotion();

    root._camstActiveIndex = 0;
    root._camstIntroImageIndex = 0;
    root._camstNavigating = false;
    root._camstWasIntro = true;

    if (reducedMotion) {
      setImpactPhase(root, false, true);
      setImpactActiveIndex(root, 0, true);
      root.dataset.camstImpactInit = '1';
      return;
    }

    setImpactPhase(root, true, false);
    setImpactActiveIndex(root, 0, reducedMotion, { animateCounter: false });
    startIntroImageLoop(root, reducedMotion);
    bindImpactScroll(root, reducedMotion);

    root.querySelectorAll('[data-camst-impact-nav-item]').forEach(function (button) {
      button.addEventListener('click', function () {
        var index = parseInt(button.getAttribute('data-index') || '0', 10);
        var isMobile = !isImpactDesktop();
        var itemCount = parseInt(root.getAttribute('data-count') || '0', 10);
        var progress = getNavScrollProgress(index, itemCount, isMobile);

        if (index === root._camstActiveIndex && root.classList.contains('is-slider')) {
          return;
        }

        stopIntroImageLoop(root);
        root._camstWasIntro = false;
        root._camstNavLockIndex = index;

        setImpactPhase(root, false, true);
        setImpactActiveIndex(root, index, reducedMotion);
        scrollToImpactProgress(root, progress, reducedMotion);
      });
    });

    root.dataset.camstImpactInit = '1';
  });
}

export function refreshCamstImpact() {
  document.querySelectorAll('[data-camst-impact][data-camst-impact-init="1"]').forEach(function (root) {
    positionImpactMarkers(root);
    updateImpactFromScroll(root, prefersReducedMotion());
  });
}
